export type AllowedFileType = "image" | "document" | "pdf" | "excel" | "image-and-pdf";

export interface FileValidationConfig {
  maxSize: number; // in bytes
  allowedTypes: string[];
  allowedExtensions: string[];
}

export const FILE_VALIDATION_CONFIGS: Record<AllowedFileType, FileValidationConfig> = {
  image: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    allowedExtensions: [".jpg", ".jpeg", ".png"],
  },
  document: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/pdf"],
    allowedExtensions: [".doc", ".docx", ".pdf"],
  },
  pdf: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["application/pdf"],
    allowedExtensions: [".pdf"],
  },
  "image-and-pdf": {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["image/jpeg", "image/png", "application/pdf"],
    allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf"],
  },
  excel: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    allowedExtensions: [".xls", ".xlsx"],
  },
} as const;

export const validateFile = (file: File, fileType: AllowedFileType): string | null => {
  const config = FILE_VALIDATION_CONFIGS[fileType];

  // Type validation
  if (config.allowedTypes[0] !== "*/*" && !config.allowedTypes.includes(file.type)) {
    return `Invalid file type. Allowed types: ${config.allowedExtensions.join(", ")}`;
  }
  // Size validation
  if (file.size > config.maxSize) {
    return `File size should be less than ${config.maxSize / (1024 * 1024)}MB`;
  }
  return null;
};

export const FILE_EXTENSION_ICON_MAP: Record<string, string> = {
  png: "/icons/file-icon-png.svg",
  jpg: "/icons/file-icon-jpg.svg",
  jpeg: "/icons/file-icon-jpeg.svg",
  gif: "/icons/file-type-icon-gif.svg",
  pdf: "/icons/file-icon-pdf.svg",
  doc: "/icons/file-type-icon-docx.svg",
  docx: "/icons/file-type-icon-docx.svg",
  xls: "/icons/file-type-icon-xlsx.svg",
  xlsx: "/icons/file-type-icon-xlsx.svg",
} as const;

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const getFileExtension = (url: string): string => {
  if (!url) return "";
  const extension = url.split(".").pop()?.toLowerCase() || "";
  return extension.split("?")[0];
};

export const getExtensionType = (fileUrl: string): AllowedFileType | "video" | null => {
  const extension = getFileExtension(fileUrl);

  if (!extension) return null;

  // Check image extensions
  if (["jpg", "jpeg", "png"].includes(extension)) {
    return "image";
  }

  // Check PDF
  if (extension === "pdf") {
    return "pdf";
  }

  // Check document extensions
  if (["doc", "docx"].includes(extension)) {
    return "document";
  }

  // Check excel extensions
  if (["xls", "xlsx"].includes(extension)) {
    return "excel";
  }
  if (["mp4"].includes(extension)) {
    return "video";
  }
  return null;
};
